import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import {
  Box,
  BoxProps,
  Button,
  Card,
  CardActionArea,
  CardActionAreaProps,
  Container,
  Stack,
  StackProps,
  Typography,
  TypographyProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { TP_COLORS } from '../../../constants';
import {
  isMobileScreen,
  getUrlConvertedString,
  getSubjectIconName,
  getSubjectColorNew,
  checkSubjectCodeHide,
} from '../../../helper';
import { useAppSelector } from '../../../hooks';

import { getAuthState } from '../../../store/AuthSlice';
import {
  ICurriculumForHome,
  ISubjectForHome,
  getCurriculumState,
  getCurriculumsForHomePage,
} from '../../../store/CurriculumSlice';
import {
  getTopicState,
  getTopicsWithSubTopics,
} from '../../../store/TopicSlice';
import { IGroupedTopic } from '../../../store/SubTopicSlice';
import {
  getProgressState,
  getSubjectProgress,
} from '../../../store/ProgressSlice';

import SubjectProgressBar from './components/SubjectProgressBar';
import CircularProgressWithValue from './components/CircularProgressWithValue';
import Breadcrumbs, {
  BreadcrumbOption,
} from '../../../components/BreadCrumb/Breadcrumbs';
import AssessmentTab from './components/AssessmentTab';
import HomeFooter from '../../Home/components/HomeFooter';
import FullScreenLoader from '../../../components/Loaders/FullScreenloader';
import SubTopicList from './components/SubTopicList';
import NotFound from '../../Validate/NotFound';

const topStackProps: StackProps['sx'] = {
  direction: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  style: { padding: '30px 0' },
};

const subjectBoxProps: BoxProps = {
  display: 'flex',
  alignItems: 'center',
  sx: {
    justifyContent: { xs: 'space-between', sm: 'flex-start' },
  },
};

const subjectNameTypoProps: TypographyProps['sx'] = {
  component: 'h1',
  variant: 'h5',
  fontSize: '30px',
  marginRight: '20px',
};

const subjectDescriptionTypoProps: TypographyProps['sx'] = {
  component: 'h2',
  variant: 'subtitle2',
  fontWeight: '600',
  marginTop: '12px',
  maxWidth: '800px',
};

const subjectCodeTypoProps: TypographyProps['sx'] = {
  variant: 'h6',
  fontSize: '18px',
  margin: '20px',
  fontWeight: '600',
};

const cardActionAreaStyles: CardActionAreaProps['sx'] = {
  display: 'flex',
  justifyContent: 'flex-start',
  padding: '20px',
};

const topicNameTypoProps: TypographyProps['sx'] = {
  variant: 'h6',
  fontSize: '18px',
  color: TP_COLORS.gray.dark,
};

const assessmentStackProps: StackProps['sx'] = {
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'row',
  sx: {
    minHeight: { xs: '60px', sm: '90px' },
    background: `url('${process.env.PUBLIC_URL}/images/subjectPage/shape-half-circle-1.png') top left/contain no-repeat, url('${process.env.PUBLIC_URL}/images/subjectPage/shape-half-circle-2.png') top right/contain no-repeat, #E5F7F6`,
  },
};

export const ellipsisTwoLineTypoStyles: TypographyProps['sx'] = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: '2',
  WebkitBoxOrient: 'vertical',
  color: TP_COLORS.gray.light,
};

const constructBreadcrumbOptions = (state: IState) => {
  const options: Array<BreadcrumbOption> = [
    {
      type: 'link',
      title: 'Home',
      link: `${process.env.PUBLIC_URL}`,
    },
  ];

  if (state.selectedCurriculum) {
    const curriculum = getUrlConvertedString(state.selectedCurriculum.name);
    options.push({
      type: 'link',
      title: state.selectedCurriculum.name,
      link: `${process.env.PUBLIC_URL}/${curriculum}`,
    });
  }

  if (state.selectedSubject) {
    options.push({
      type: 'text',
      title: state.selectedSubject.name,
      highlight: true,
    });
  }

  return options;
};

interface IState {
  selectedCurriculum: ICurriculumForHome | undefined;
  selectedSubject: ISubjectForHome | undefined;
}

const initialState: IState = {
  selectedCurriculum: undefined,
  selectedSubject: undefined,
};

const SubjectPage: React.FC = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = isMobileScreen();
  const theme = useTheme();
  const belowMd = useMediaQuery(theme.breakpoints.down('md'));

  // Store
  const authState = useAppSelector(getAuthState);
  const topicState = useAppSelector(getTopicState);
  const progressState = useAppSelector(getProgressState);
  const curriculumState = useAppSelector(getCurriculumState);

  const { curriculumsForHomePage } = curriculumState;
  const { topicsWithSubTopics, status: topicStatus } = topicState;
  const { subjectProgress } = progressState;
  const userRole = authState.user?.role.name;

  // State
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [state, setState] = useState<IState>(initialState);
  const [isExpand, setIsExpand] = useState(true);
  const [selectedTopic, setSelectedTopic] = useState<IGroupedTopic>();
  const [topicList, setTopicList] = useState<Array<IGroupedTopic>>([]);
  const [assessmentList, setAssessmentList] = useState<Array<IGroupedTopic>>(
    []
  );

  const subjectColor = getSubjectColorNew(state.selectedSubject?.name || '');
  const breadcrumbOptions = constructBreadcrumbOptions(state);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (curriculumsForHomePage.length === 0) {
      dispatch(getCurriculumsForHomePage());
    }

    if (isMobile) {
      setIsExpand(false);
    }
  }, []);

  useEffect(() => {
    if (curriculumsForHomePage.length) {
      const selectedCurriculum = curriculumsForHomePage.find(
        (curriculumItem) =>
          getUrlConvertedString(curriculumItem.name) === params.curriculum
      );

      if (selectedCurriculum && selectedCurriculum.subjects.length) {
        const selectedSubject = selectedCurriculum.subjects.find(
          (subjectItem) =>
            getUrlConvertedString(subjectItem.name) === params.subject &&
            getUrlConvertedString(subjectItem.subjectLevel) ===
              params.subjectLevel &&
            getUrlConvertedString(subjectItem.subjectCode) ===
              params.subjectCode
        );

        if (selectedSubject) {
          setState({
            selectedCurriculum,
            selectedSubject,
          });
        }
      }
      setInitialLoading(false);
    }
  }, [params]);

  useEffect(() => {
    if (
      topicStatus !== 'loading' &&
      state.selectedCurriculum &&
      state.selectedSubject &&
      checkTopicsFetched()
    ) {
      dispatch(
        getTopicsWithSubTopics({
          curriculum: state.selectedCurriculum.name,
          subject: state.selectedSubject.name,
          subjectCode: state.selectedSubject.subjectCode,
          subjectLevel: state.selectedSubject.subjectLevel,
        })
      );
    }
  }, [state.selectedSubject]);

  useEffect(() => {
    if (topicsWithSubTopics.length) {
      const topics = topicsWithSubTopics.filter(
        (item) => !item.isAssessment && item.name !== 'Overview'
      );

      if (topics.length) {
        setTopicList(topics);
        if (!isMobile) {
          setSelectedTopic(topics[0]);
        }
      }

      setAssessmentList(
        topicsWithSubTopics.filter((item) => item.isAssessment)
      );

      if (
        userRole === 'Student' &&
        state.selectedCurriculum &&
        state.selectedSubject
      ) {
        dispatch(
          getSubjectProgress({
            curriculum: state.selectedCurriculum.name,
            subject: state.selectedSubject.name,
            subjectLevel: state.selectedSubject.subjectLevel,
          })
        );
      }
    }
  }, [topicsWithSubTopics]);

  const checkTopicsFetched = () => {
    if (
      state.selectedSubject &&
      topicsWithSubTopics.length &&
      topicsWithSubTopics[0].subject === state.selectedSubject.name &&
      topicsWithSubTopics[0].subjectLevel ===
        state.selectedSubject.subjectLevel &&
      topicsWithSubTopics[0].subjectCode === state.selectedSubject.subjectCode
    ) {
      return false;
    }
    return true;
  };

  const getTopicProgress = (topicData: IGroupedTopic) => {
    let progressValue = 0;

    if (subjectProgress) {
      const topicProgress = subjectProgress.topics.find(
        (item) => item._id === topicData._id
      );

      if (topicProgress && topicProgress.subTopics.length) {
        const { subTopics } = topicProgress;

        const totalProgress = subTopics.reduce((accumulator, object) => {
          return accumulator + object.completion;
        }, 0);

        progressValue = totalProgress / (topicData.subTopics.length || 1);
      }
    }
    return progressValue;
  };

  const getTopicDescription = (topic: IGroupedTopic) => {
    const filteredSubTopics: string[] = [];

    if (topic.subTopics.length) {
      topic.subTopics.forEach((subTopic) => {
        if (
          subTopic.name.toLocaleLowerCase().search('worksheet') < 0 &&
          subTopic.name.toLocaleLowerCase().search('overview') < 0 &&
          subTopic.name.toLocaleLowerCase().search('topic planner')
        ) {
          filteredSubTopics.push(subTopic.name);
        }
      });
    }

    return `The ${
      topic.name
    } topic covers fundamental concepts such as ${filteredSubTopics
      .map((subTopicName) => subTopicName)
      .join(', ')}.`;
  };

  const handleOverviewClick = () => {
    const overviewTopic = topicsWithSubTopics.find(
      (item) => item.name === 'Overview'
    );

    if (overviewTopic) {
      const overviewTopicName = getUrlConvertedString(overviewTopic.name);
      navigate(`${overviewTopicName}/${overviewTopic._id}`);
    }
  };

  const isOverviewAvailable = () =>
    !topicsWithSubTopics.find((item) => item.name === 'Overview');

  let metaTitle =
    'IGCSE And GCSE - Maths And Science Questions with Answers By Topic | Tutopiya';

  let metaDesc =
    'Science and Maths questions with answers organized by topic. Our questions and answers are designed to help students prepare for their exams. Give them a try today!';

  if (state.selectedCurriculum) {
    metaTitle = `${
      state.selectedCurriculum.name || ''
    } - Maths And Science Questions with Answers By Topic | Tutopiya`;

    metaDesc = `${
      state.selectedCurriculum.name || ''
    } maths and science questions with answers organized by topic. Our questions and answers are designed to help students prepare for their exams. Give them a try today!`;
  }

  if (initialLoading || topicStatus === 'loading') {
    return <FullScreenLoader />;
  }

  if (!initialLoading && !state.selectedSubject) {
    return <NotFound />;
  }

  return (
    <Box>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
      </Helmet>

      <div style={{ minHeight: '80vh' }}>
        <Container maxWidth="lg" sx={{ mb: '60px', pt: '10px' }}>
          <Stack {...topStackProps}>
            <Breadcrumbs
              options={breadcrumbOptions}
              showDropDown={belowMd}
              textColor={TP_COLORS.gray.light}
              highlightColor={subjectColor}
            />
            {userRole === 'Student' && <SubjectProgressBar />}
          </Stack>
          <Box
            display="flex"
            justifyContent="space-between"
            sx={{
              color: TP_COLORS.white,
              borderRadius: '7px',
              background: `url('${process.env.PUBLIC_URL}/images/subjectPage/shape-header.png') top right/contain no-repeat, ${subjectColor}`,
            }}
          >
            <Box padding="24px">
              <Box {...subjectBoxProps}>
                <Typography {...subjectNameTypoProps}>
                  {state.selectedSubject?.name || ''}
                </Typography>
                <Button
                  color="inherit"
                  variant="contained"
                  sx={{
                    color: subjectColor,
                    backgroundColor: TP_COLORS.white,
                    fontWeight: '600',
                    '&:disabled': {
                      opacity: 0.5,
                      backgroundColor: TP_COLORS.white,
                      color: subjectColor,
                    },
                  }}
                  onClick={handleOverviewClick}
                  disabled={isOverviewAvailable()}
                >
                  Overview
                </Button>
              </Box>

              <Typography {...subjectDescriptionTypoProps}>
                Access comprehensive {state.selectedCurriculum?.name || ''}{' '}
                {state.selectedSubject?.name || ''} resources including
                chapter-wise Q&A, past papers, video lessons, quizzes, and
                expert guidance for exam success, curated by experienced
                teachers and examiners. Achieve top grades with ease!
              </Typography>
            </Box>
            <Box display="flex" sx={{ display: { xs: 'none', sm: 'flex' } }}>
              <Stack justifyContent="center">
                <img
                  src={`${
                    process.env.PUBLIC_URL
                  }/images/landingPage/icon-${getSubjectIconName(
                    state.selectedSubject?.name || ''
                  )}-white.avif`}
                  alt="subject-img"
                  style={{ height: '140px' }}
                />
              </Stack>

              {!checkSubjectCodeHide(state.selectedCurriculum?.name || '') && (
                <Typography {...subjectCodeTypoProps}>
                  #{state.selectedSubject?.subjectCode || ''}
                </Typography>
              )}
            </Box>
          </Box>

          <Box marginTop="30px" display="flex">
            <Stack
              sx={{
                width: { xs: '100%', sm: '50%', md: '60%', lg: '65%' },
              }}
            >
              {topicList.length
                ? topicList
                    .slice(0, isExpand ? topicList.length : 5)
                    .map((topicItem, topicIndex) => {
                      const isSelected = selectedTopic === topicItem;
                      return (
                        <Card
                          key={topicIndex}
                          elevation={0}
                          sx={{
                            backgroundColor: isSelected
                              ? `${subjectColor}1A`
                              : TP_COLORS.paperGray.light,
                            borderRadius: '10px',
                          }}
                        >
                          <CardActionArea
                            sx={cardActionAreaStyles}
                            onClick={() => {
                              const topicName = getUrlConvertedString(
                                topicItem.name
                              );
                              navigate(`${topicName}/${topicItem._id}`);
                            }}
                            onMouseOver={() => setSelectedTopic(topicItem)}
                          >
                            <CircularProgressWithValue
                              value={getTopicProgress(topicItem) || 0}
                              progressProps={{ marginRight: '5px' }}
                              color={subjectColor}
                            />
                            <Box marginLeft="20px">
                              <Typography {...topicNameTypoProps}>
                                {topicItem.name}
                              </Typography>
                              <Typography
                                variant="subtitle2"
                                sx={ellipsisTwoLineTypoStyles}
                              >
                                {getTopicDescription(topicItem)}
                              </Typography>
                            </Box>
                          </CardActionArea>
                        </Card>
                      );
                    })
                : null}
            </Stack>

            {/* SubTopics Section */}
            {selectedTopic && <SubTopicList selectedTopic={selectedTopic} />}
          </Box>

          {/* View more button  */}
          {isMobile && topicList.length > 5 ? (
            <Stack alignItems="center" marginTop="10px">
              <Button
                color="secondary"
                onClick={() => setIsExpand((prevState) => !prevState)}
              >
                View {isExpand ? 'less' : 'more'}
              </Button>
            </Stack>
          ) : null}
        </Container>

        {/* Test Your Skills */}
        {assessmentList.length ? (
          <>
            <Stack {...assessmentStackProps}>
              <Typography
                variant={isMobile ? 'subtitle1' : 'h5'}
                fontWeight="600"
              >
                Test Your Skills
              </Typography>
            </Stack>
            <Container maxWidth="lg" sx={{ mt: '50px', mb: '60px' }}>
              <AssessmentTab
                assessmentList={assessmentList}
                subjectProgress={subjectProgress}
              />
            </Container>
          </>
        ) : null}
      </div>

      <HomeFooter />
    </Box>
  );
};

export default SubjectPage;
